import { useState } from 'react';
import { useDebouncedValue } from '../hooks/useDebouncedValue';
import { Link } from 'react-router-dom';
import { UserPlus } from 'lucide-react';
import { useDepartments } from '../hooks/useDepartments';
import { useOrgChart, usePeople } from '../hooks/usePeople';
import { usePermissions } from '../hooks/usePermissions';
import { useI18n } from '../contexts/I18nContext';
import { useAuthStore } from '../store/useAuthStore';
import { PeopleGrid } from '../components/people/PeopleGrid';
import { PeopleListTable } from '../components/people/PeopleListTable';
import { OrgChart } from '../components/people/OrgChart';
import { CreateTeamMemberModal } from '../components/people/CreateTeamMemberModal';
import { Select } from '../components/ui/Select';
import { FadeIn, SkeletonCardGrid } from '../components/ui/Skeleton';

type TeamView = 'chart' | 'list';

export function PeoplePage() {
  // Phones open on the list: the org chart starts zoomed out to ~25% there,
  // names too small to read until you pinch. The toggle still switches.
  const [view, setView] = useState<TeamView>(() =>
    window.matchMedia('(max-width: 639px)').matches ? 'list' : 'chart',
  );
  const [editing, setEditing] = useState(false);
  const [search, setSearch] = useState('');
  const [departmentId, setDepartmentId] = useState('');
  // Search as the user types, without a request per keystroke.
  const debouncedSearch = useDebouncedValue(search.trim());
  const [showAddMember, setShowAddMember] = useState(false);

  const isAdmin = useAuthStore((s) => s.user?.role === 'ADMIN');
  const { can } = usePermissions();
  const { t } = useI18n();
  const canCreatePeople = can('people', 'canCreate');
  const canEditPeople = can('people', 'canEdit');

  const {
    data: people,
    isLoading,
    isError,
  } = usePeople({
    search: debouncedSearch || undefined,
    departmentId: departmentId || undefined,
  });

  const { data: orgChart, isLoading: orgLoading, isError: orgError } = useOrgChart();

  // Configurable departments (CLE-DASH) drive the filter options.
  const { data: departments } = useDepartments();

  return (
    <div className="w-full p-4 sm:p-6 lg:p-8">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <h1 className="text-2xl font-bold text-gray-900 sm:text-3xl">{t('people.title')}</h1>
        {/* Phones: the two actions as equal halves of the row. */}
        <div className="grid grid-cols-2 gap-2 sm:flex sm:flex-wrap sm:items-center [&>*:last-child:nth-child(odd)]:col-span-2">
          {canCreatePeople && (
            <button
              type="button"
              onClick={() => setShowAddMember(true)}
              className="inline-flex items-center justify-center gap-1.5 rounded bg-brand-600 px-4 py-2 text-sm font-medium text-white hover:bg-brand-700"
            >
              <UserPlus size={15} />
              {t('people.addMember')}
            </button>
          )}
          <Link
            to="/people/capacity"
            className="inline-flex items-center justify-center rounded border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
          >
            <span className="sm:hidden">{t('people.capacityShort')}</span>
            <span className="hidden sm:inline">{t('people.capacityPlanning')}</span>
          </Link>
        </div>
      </div>

      {/* View toggle: org chart vs list. */}
      {/* Phones: the view toggle across the row (two equal halves), the edit
          button full width under it. */}
      <div className="mt-6 flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center sm:gap-3">
        <div
          role="tablist"
          aria-label={t('people.view.label')}
          className="grid grid-cols-2 rounded-md border border-gray-300 p-0.5 sm:inline-flex"
        >
          <button
            type="button"
            role="tab"
            aria-selected={view === 'chart'}
            onClick={() => setView('chart')}
            className={`rounded px-3 py-1.5 text-sm font-medium transition ${
              view === 'chart' ? 'bg-brand-600 text-white' : 'text-gray-600 hover:bg-gray-50'
            }`}
          >
            {t('people.view.chart')}
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={view === 'list'}
            onClick={() => {
              setView('list');
              setEditing(false);
            }}
            className={`rounded px-3 py-1.5 text-sm font-medium transition ${
              view === 'list' ? 'bg-brand-600 text-white' : 'text-gray-600 hover:bg-gray-50'
            }`}
          >
            {t('people.view.list')}
          </button>
        </div>

        {/* Admin-only edit toggle, available in chart view. */}
        {view === 'chart' && isAdmin && (
          <button
            type="button"
            onClick={() => setEditing((e) => !e)}
            className={`rounded border px-3 py-1.5 text-sm font-medium transition ${
              editing
                ? 'border-brand-600 bg-brand-600 text-white hover:bg-brand-700'
                : 'border-gray-300 text-gray-700 hover:bg-gray-50'
            }`}
          >
            {editing ? t('people.editOrgDone') : t('people.editOrg')}
          </button>
        )}

        {/* Inline edit toggle for the list view (department + allocation hours). */}
        {view === 'list' && canEditPeople && (
          <button
            type="button"
            onClick={() => setEditing((e) => !e)}
            className={`rounded border px-3 py-1.5 text-sm font-medium transition ${
              editing
                ? 'border-brand-600 bg-brand-600 text-white hover:bg-brand-700'
                : 'border-gray-300 text-gray-700 hover:bg-gray-50'
            }`}
          >
            {editing ? t('people.editListDone') : t('people.editList')}
          </button>
        )}
      </div>

      {/* List-view filters (search + department). */}
      {view === 'list' && (
        <div className="mt-4 flex flex-col gap-3 sm:flex-row sm:flex-wrap">
          <input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t('people.search')}
            className="ui-input sm:w-64"
          />
          <Select
            label={t('people.filter.allDepartments')}
            hideLabel
            clearable
            value={departmentId}
            onChange={setDepartmentId}
            placeholder={t('people.filter.allDepartments')}
            options={(departments ?? []).map((d) => ({
              value: d.id,
              label: d.name,
              color: d.color,
            }))}
            className="w-full sm:w-52"
          />
        </div>
      )}

      <div className="mt-6">
        {view === 'chart' ? (
          <>
            {orgLoading && <SkeletonCardGrid count={4} />}
            {orgError && <p className="text-red-600">{t('people.error.org')}</p>}
            {orgChart && (
              <FadeIn>
                <OrgChart data={orgChart} editable={editing && isAdmin} />
              </FadeIn>
            )}
          </>
        ) : (
          <>
            {isLoading && <SkeletonCardGrid count={8} />}
            {isError && <p className="text-red-600">{t('people.error.directory')}</p>}
            {people &&
              (editing && canEditPeople ? (
                <FadeIn>
                  <PeopleListTable people={people} departments={departments ?? []} editable />
                </FadeIn>
              ) : (
                <FadeIn>
                  <PeopleGrid people={people} />
                </FadeIn>
              ))}
          </>
        )}
      </div>

      <CreateTeamMemberModal open={showAddMember} onClose={() => setShowAddMember(false)} />
    </div>
  );
}
